import { t } from "@lingui/core/macro";
import { countries } from "countries-list";
import ReactCountryFlag from "react-country-flag";
import { MantineFormValues } from "./shared.types";
import { LimeAutocomplete, LimeAutocompleteProps } from "./LimeAutocomplete";
import { LimeAutocompleteV3 } from "./LimeAutocompleteV3";
import { LimeSelect, LimeSelectProps } from "./LimeSelect";
import { LimeSelectV3 } from "./LimeSelectV3";
import { AutocompleteItem } from "@heroui/react";
import { useMemo } from "react";

type LimeCountryPickerSingleProps = Omit<
  MantineFormValues & LimeAutocompleteProps<{ label: string; key: string }>,
  "children" | "items" | "onChange" | "value" | "variant"
> & {
  selectionMode?: "single";
  value?: string;
  onChange: (value: string) => void;
};

type LimeCountryPickerMultipleProps = Omit<
  MantineFormValues & LimeSelectProps,
  | "children"
  | "items"
  | "onChange"
  | "value"
  | "onSingleItemChange"
  | "onMultipleItemChange"
  | "selectionMode"
  | "variant"
> & {
  selectionMode: "multiple";
  value?: string[];
  onChange: (value: string[]) => void;
  isVirtualized?: boolean;
};

type LimeCountryPickerSharedProps = (
  | LimeCountryPickerSingleProps
  | LimeCountryPickerMultipleProps
) & {
  filteredCountries?: (keyof typeof countries)[];
  "data-identifier"?: string;
};

type LimeCountryPickerProps = LimeCountryPickerSharedProps &
  (
    | { base?: "heroui-v2"; variant?: "bordered" | "faded" | "flat" | "underlined" }
    | { base: "heroui-v3"; variant?: "primary" | "secondary" }
  );

const priorityCountries: (keyof typeof countries)[] = [
  "SI",
  "HR",
  "RS",
  "BA",
  "DE",
  "IT",
  "FR",
  "ES",
];

export const LimeCountryPicker = (props: LimeCountryPickerProps) => {
  const { value, onChange, error, selectionMode = "single" } = props;

  const priorityCountriesItems = useMemo(() => {
    return priorityCountries.map((country) => ({
      label: countries[country].name,
      key: country,
      startContent: (
        <ReactCountryFlag
          countryCode={country}
          aria-label={`Flag of ${countries[country].name}`}
        />
      ),
    }));
  }, []);

  const otherCountriesItems = useMemo(() => {
    return Object.entries(countries).flatMap(([key, country]) =>
      priorityCountries.includes(key as keyof typeof countries)
        ? []
        : [
            {
              label: country.name,
              key,
              startContent: (
                <ReactCountryFlag
                  countryCode={key}
                  aria-label={`Flag of ${country.name}`}
                />
              ),
            },
          ],
    );
  }, []);

  const countryItems = useMemo(() => {
    const filteredCountries = props.filteredCountries;

    if (!filteredCountries) {
      return [...priorityCountriesItems, ...otherCountriesItems];
    }

    return [...priorityCountriesItems, ...otherCountriesItems].filter(
      (country) =>
        filteredCountries.includes(country.key as keyof typeof countries),
    );
  }, [priorityCountriesItems, otherCountriesItems, props.filteredCountries]);

  if (selectionMode === "multiple") {
    if (props.base === "heroui-v3") {
      return (
        <LimeSelectV3
          className={props.className}
          variant={props.variant}
          showSelectAllToggle
          disallowEmptySelection={false}
          label={t`Države`}
          items={countryItems}
          value={value as string[]}
          selectionMode="multiple"
          onMultipleItemChange={(values) => {
            (onChange as (value: string[]) => void)(values);
          }}
          error={error}
          data-identifier={props["data-identifier"]}
        />
      );
    }

    return (
      <LimeSelect
        className={props.className}
        classNames={props.classNames}
        variant={props.variant}
        size={props.size}
        isVirtualized={
          props.isVirtualized != null
            ? props.isVirtualized
            : countryItems.length > 10
        }
        showSelectAllToggle
        disallowEmptySelection={false}
        label={t`Države`}
        items={countryItems}
        value={value as string[]}
        selectionMode="multiple"
        onMultipleItemChange={(values) => {
          (onChange as (value: string[]) => void)(values);
        }}
        error={error}
        data-identifier={props["data-identifier"]}
      />
    );
  }

  if (props.base === "heroui-v3") {
    return (
      <LimeAutocompleteV3
        fullWidth
        label={t`Država`}
        items={countryItems.map((item) => ({
          id: item.key,
          label: item.label,
          startContent: item.startContent,
        }))}
        value={(value as string) ?? null}
        onChange={(v) => {
          if (v) (onChange as (value: string) => void)(v);
        }}
        error={error}
        data-identifier={props["data-identifier"]}
        variant={props.variant}
      />
    );
  }

  return (
    <LimeAutocomplete
      label={t`Država`}
      defaultItems={countryItems}
      selectedKey={value as string}
      onSelectionChange={(value) => {
        if (value) {
          (onChange as (value: string) => void)(String(value));
        }
      }}
      errorMessage={error}
      isInvalid={!!error}
      data-identifier={props["data-identifier"]}
      variant={props.variant}
      size={props.size}
    >
      {(item) => (
        <AutocompleteItem
          key={item.key}
          startContent={<ReactCountryFlag countryCode={item.key} />}
        >
          {item.label}
        </AutocompleteItem>
      )}
    </LimeAutocomplete>
  );
};
